import { useState, useEffect } from 'react';
import axios from 'axios';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Card } from '@/components/ui/card';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Which leagues have group A / B splits
const GROUPED_LEAGUES = ['Champions League', 'Icons League'];

function StandingsBlock({ standings, title }) {
  if (standings.length === 0) {
    return (
      <div>
        <h4 className="text-lg font-bold text-[#D4AF37] mb-3">{title}</h4>
        <p className="text-[#9BA3AF] text-sm">No teams in this group yet.</p>
      </div>
    );
  }
  return (
    <div>
      <h4 className="text-lg font-bold text-[#D4AF37] mb-3">{title}</h4>
      <div className="overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow className="border-white/10 hover:bg-transparent">
              <TableHead className="text-[#D4AF37] font-bold">#</TableHead>
              <TableHead className="text-[#D4AF37] font-bold">Team</TableHead>
              <TableHead className="text-[#D4AF37] font-bold text-center">MP</TableHead>
              <TableHead className="text-[#D4AF37] font-bold text-center">W</TableHead>
              <TableHead className="text-[#D4AF37] font-bold text-center">D</TableHead>
              <TableHead className="text-[#D4AF37] font-bold text-center">L</TableHead>
              <TableHead className="text-[#D4AF37] font-bold text-center">GF</TableHead>
              <TableHead className="text-[#D4AF37] font-bold text-center">GA</TableHead>
              <TableHead className="text-[#D4AF37] font-bold text-center">GD</TableHead>
              <TableHead className="text-[#D4AF37] font-bold text-center">Pts</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {standings.map((team) => (
              <TableRow
                key={team.team_id}
                data-testid={`standings-row-${team.team_id}`}
                className="border-white/5 hover:bg-white/5"
              >
                <TableCell className="text-white font-bold">
                  {team.rank <= 2 ? (
                    <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-[#D4AF37] text-[#0A0F1C] text-xs">
                      {team.rank}
                    </span>
                  ) : (
                    team.rank
                  )}
                </TableCell>
                <TableCell className="text-white font-semibold">{team.team}</TableCell>
                <TableCell className="text-white text-center font-mono">{team.mp}</TableCell>
                <TableCell className="text-[#22C55E] text-center font-mono">{team.w}</TableCell>
                <TableCell className="text-[#EAB308] text-center font-mono">{team.d}</TableCell>
                <TableCell className="text-[#EF4444] text-center font-mono">{team.l}</TableCell>
                <TableCell className="text-white text-center font-mono">{team.gf}</TableCell>
                <TableCell className="text-white text-center font-mono">{team.ga}</TableCell>
                <TableCell className="text-white text-center font-mono">
                  {team.gd > 0 ? `+${team.gd}` : team.gd}
                </TableCell>
                <TableCell className="text-[#D4AF37] text-center font-mono font-bold text-lg">
                  {team.pts}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}

export default function StandingsTable({ ageGroup }) {
  const [groupA, setGroupA] = useState([]);
  const [groupB, setGroupB] = useState([]);
  const [single, setSingle] = useState([]);
  const [initialLoading, setInitialLoading] = useState(true);

  const hasGroups = GROUPED_LEAGUES.includes(ageGroup);

  useEffect(() => {
    setInitialLoading(true);
  }, [ageGroup]);

  useEffect(() => {
    const fetchStandings = async () => {
      try {
        if (hasGroups) {
          const [a, b] = await Promise.all([
            axios.get(`${API}/standings?age_group=${encodeURIComponent(ageGroup)}&group=A`),
            axios.get(`${API}/standings?age_group=${encodeURIComponent(ageGroup)}&group=B`),
          ]);
          setGroupA(a.data);
          setGroupB(b.data);
        } else {
          const { data } = await axios.get(`${API}/standings?age_group=${encodeURIComponent(ageGroup)}`);
          setSingle(data);
        }
      } catch (error) {
        console.error('Failed to fetch standings:', error);
      } finally {
        setInitialLoading(false);
      }
    };

    fetchStandings();
    const interval = setInterval(fetchStandings, 15000);
    return () => clearInterval(interval);
  }, [ageGroup, hasGroups]);

  return (
    <Card className="bg-[#131C2F] border border-white/10 p-6" data-testid="standings-table">
      <h3 className="text-2xl font-bold text-white mb-6">League Table - {ageGroup}</h3>
      {initialLoading ? (
        <p className="text-[#9BA3AF]">Loading standings...</p>
      ) : hasGroups ? (
        <div className="space-y-8">
          <StandingsBlock standings={groupA} title="Group A" />
          <StandingsBlock standings={groupB} title="Group B" />
        </div>
      ) : single.length === 0 ? (
        <p className="text-[#9BA3AF]">No teams registered for this league yet.</p>
      ) : (
        <StandingsBlock standings={single} title="Group Stage" />
      )}
    </Card>
  );
}
